import React, { useEffect, useState } from "react";
import { Box, Typography, IconButton, Collapse, Switch, TextField, MenuItem, Select, CircularProgress } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { getAlertConfig } from "../../../../../services/apis";
import { AlertConfigResponse } from "../../../../../types/Alerts/AlertTypes";
import AlertRecipients from "./AlertRecipients";
import { defaultAlertConfig } from "./AlertHelper";

const AlertsSection = ({
  unitID,
  // pace_device_serial_number,
  alertConfig,
  setAlertConfig,
  setUserChangesFlag,
}: {
  unitID: number | string;
  // pace_device_serial_number: number | string;
  alertConfig: AlertConfigResponse;
  setAlertConfig: Function;
  setUserChangesFlag: Function;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Extract alert types from the alert config
  const connectiviyLossAlert = alertConfig.site_alerts.loss_connection.enabled;
  const demandResponse = alertConfig.site_alerts.demand_response.enabled;
  const unitAlerts = alertConfig.unit_alerts;

  const handleToggleExpand = () => setIsExpanded(!isExpanded);

  useEffect(() => {
    const fetchAlertConfig = async () => {
      setLoading(true);
      try {
        const response = await getAlertConfig(unitID);
        setAlertConfig(response);
      } catch (err) {
        console.error("Error fetching alert config", err);
        setAlertConfig({ ...defaultAlertConfig, unit_id: unitID }); // Fallback to default values
      } finally {
        setLoading(false);
      }
    };

    // If pace_device_serial_number is valid, fetch data, else set to default
    if (unitID !== 0 && unitID !== "") {
      fetchAlertConfig();
    } else {
      setAlertConfig({ ...defaultAlertConfig, unit_id: unitID });
    }

    // Reset alertConfig on unmount or when unitID changes
    return () => setAlertConfig({ ...defaultAlertConfig, unit_id: unitID });
  }, [unitID]);

  const handleSiteConfigChange = (field: keyof AlertConfigResponse["site_alerts"], value: boolean) => {
    setAlertConfig({
      ...alertConfig,
      site_alerts: {
        ...alertConfig.site_alerts,
        [field]: { enabled: value },
      },
    });
    setUserChangesFlag(true);
  };

  useEffect(() => {
    console.log(alertConfig, "----------------");
  }, [alertConfig]);

  const handleUnitConfigChange = (index: number, updatedValues: Partial<(typeof unitAlerts)[number]>) => {
    setAlertConfig((prevConfig: any) => ({
      ...prevConfig,
      unit_alerts: prevConfig.unit_alerts.map((alert: any, i: number) => (i === index ? { ...alert, ...updatedValues } : alert)),
    }));
    setUserChangesFlag(true);
  };

  const defaultRecipient = {
    email: "",
    name: "",
    phone_number: { country_code: "1", base: "" }, // ensure object shape
    reminders: "daily", // default to a valid option
  };

  const ensureMinimumRecipients = (recipients: any[] = []) => {
    const minRecipients = 3;
    const recipientsCopy = Array.isArray(recipients) ? recipients.slice() : [];
    // Normalize recipients to have the required fields
    const normalized = recipientsCopy.map((r) => ({
      email: r?.email ?? "",
      name: r?.name ?? "",
      phone_number: r?.phone_number ?? { country_code: "1", base: "" },
      reminders: r?.reminders ?? "daily",
    }));

    if (normalized.length >= minRecipients) return normalized;
    return [...normalized, ...Array(minRecipients - normalized.length).fill(defaultRecipient)];
  };

  // centralized updater to avoid stale closure problems
  const handleRecipientChange = (index: number, updates: Partial<any>) => {
    setAlertConfig((prev: any) => {
      const next = {
        ...prev,
        reminder_recipients: prev.reminder_recipients.map((r: any, i: number) => (i === index ? { ...r, ...updates } : r)),
      };
      // mark user changes
      setUserChangesFlag(true);
      return next;
    });
  };

  useEffect(() => {
    if (alertConfig.reminder_recipients?.length < 3) {
      const normalized = ensureMinimumRecipients(alertConfig.reminder_recipients);
      setAlertConfig((prev: any) => ({
        ...prev,
        reminder_recipients: normalized,
      }));
    }
  }, [alertConfig.reminder_recipients]);

  // console.log("unipace_device_serial_numbertId", pace_device_serial_number);
  // console.log("alertConfig", alertConfig);
  // console.log("unitID", unitID);

  const returnUnitRow = (unit: any, index: number) => {
    return (
      <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
        <Switch checked={unit.enabled} onChange={(e) => handleUnitConfigChange(index, { enabled: !unit.enabled })} />
        <Typography>{unit.UI_Name} is below</Typography>
        <TextField value={unit.below} onChange={(e) => handleUnitConfigChange(index, { below: e.target.value })} variant="standard" size="small" sx={{ mx: 1, width: 50 }} />
        <Typography>°F is above</Typography>
        <TextField value={unit.above} onChange={(e) => handleUnitConfigChange(index, { above: e.target.value })} variant="standard" size="small" sx={{ mx: 1, width: 50 }} />
        <Typography>°F for</Typography>

        <Select
          value={unit.duration_min}
          onChange={(e) =>
            handleUnitConfigChange(index, {
              ...unit,
              duration_min: e.target.value,
            })
          }
          variant="standard"
          size="small"
          sx={{ mx: 1, width: 60 }}
        >
          <MenuItem value="60">1 hr</MenuItem>
          <MenuItem value="120">2 hr</MenuItem>
        </Select>
      </Box>
    );
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">Alerts</Typography>
        <IconButton onClick={handleToggleExpand} size="small">
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={isExpanded}>
        {loading && <CircularProgress />}
        {error && <Typography color="error">{error}</Typography>}

        <Typography variant="caption" sx={{ fontSize: "12px", color: "text.secondary", mb: 2 }}>
          Note: Changing alerts will replace your current ones, and any active alerts will be deleted.
        </Typography>

        <Typography variant="body1" sx={{ color: "text.secondary", fontWeight: "normal", mt: 3 }}>
          Send site-wide notifications when:
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
          <Switch checked={connectiviyLossAlert} onChange={() => handleSiteConfigChange("loss_connection", !connectiviyLossAlert)} />
          <Typography>The device loses connectivity</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
          <Switch checked={demandResponse} onChange={() => handleSiteConfigChange("demand_response", !demandResponse)} />
          <Typography>The device is experiencing a demand response event</Typography>
        </Box>

        <Typography variant="body1" sx={{ color: "text.secondary", fontWeight: "normal", mt: 2 }}>
          Send individual unit notifications when:
        </Typography>

        {unitAlerts.length > 0 && unitAlerts.map((unit, index) => returnUnitRow(unit, index))}

        <Typography variant="body1" sx={{ color: "text.secondary", fontWeight: "normal", mt: 3 }}>
          Recipients
        </Typography>
        <Typography variant="caption" sx={{ fontSize: "12px", color: "text.secondary", mt: 1 }}>
          Note: Reminder emails are sent again based on frequency selection{" "}
        </Typography>
        <Box sx={{ mt: 3 }}>
          <AlertRecipients recipients={alertConfig.reminder_recipients} alertConfig={alertConfig} handleRecipientChange={handleRecipientChange} />
        </Box>
      </Collapse>
    </Box>
  );
};

export default AlertsSection;
